import {
    ConfirmProps,
    showConfirmMessage,
    showErrorMessage,
    showInfoMessage
} from '../../../_shared/Messages/actions/MessageActions';
import {APIActionStatus, Form, formsStore} from '../../../stores/Form';
import messagesText, {moreInfoText} from '../../../_shared/Messages/texts';
import {messagesIds} from '../../../_shared/Messages/consts';
import {htmlFormMessagesText} from '../components/HTMLFormEditor/texts';
import {priority} from '@priority-software/priority-proto-api/lib';
import {unlockHtmlForm} from '../../../services/form/form';
import {getWcfLogin} from '../../../_shared/GWTBridge/invoke/genPage';
import {showError} from '../../Messages/actions';

export const defaultConfirmProps = () => {
    return {
        confirmButtonText: messagesText.yes,
        cancleButtonText: messagesText.cancel,
        defaultButton: 0
    };
};

export const undoApproveProps = () => {
    return {
        id: messagesIds.screens.ctrlZConfirm,
        content: messagesText.ctrlZContent,
        moreInfo: moreInfoText?.ctrlZContent,
        isRemovable: true,
        confirmButtonText: messagesText.yesDeleteChanges,
        cancleButtonText: messagesText.dontDelete,
        defaultButton: 0
    };
};

export const showFormClientSideMessage = (
    session: string,
    msgType: priority.netitems.shared.Message.Type,
    msgParams?: Partial<ConfirmProps>
) => {
    const screen = formsStore.getScreenBySession(session);
    const curLevel = screen?.activeLevel;

    const params = {
        ...defaultConfirmProps(),
        ...msgParams,
        onConfirm: async () => {
            screen?.setActiveLevel(curLevel);
            msgParams.onConfirm?.();
        },
        onClose: () => {
            msgParams.onClose?.();
            screen?.setActiveLevel(curLevel);
        }
    };

    screen?.setActiveLevel(null);

    if (msgType === priority.netitems.shared.Message.Type.Information) {
        showInfoMessage(params);
    } else if (msgType === priority.netitems.shared.Message.Type.Warning) {
        showConfirmMessage(params);
    } else if (msgType === priority.netitems.shared.Message.Type.Error) {
        showErrorMessage(params);
    }
};

export const handleHTMLFormOpenMessages = async (
    startRs: priority.netitems.form.IStartRs | null,
    parentForm: Form | null
) => {
    if (startRs?.Messages?.length > 0) {
        const isError =
            startRs.Messages?.[0]?.MType === priority.netitems.shared.Message.Type.Error;

        if (isError && parentForm) {
            return await handleStartSubFormError(startRs, parentForm);
        }
    }

    return new Promise(async resolve => {
        if (!startRs.LockBy) {
            resolve(APIActionStatus.passed);
        } else {
            const lockedByOtherUser = startRs.LockBy !== getWcfLogin();

            if (lockedByOtherUser) {
                showFormClientSideMessage(
                    startRs.Session,
                    priority.netitems.shared.Message.Type.Information,
                    {
                        title: htmlFormMessagesText.lockedByAnotherUser.replace(
                            '{user}',
                            startRs.LockBy
                        ),
                        confirmButtonText: htmlFormMessagesText.approveLabel,
                        defaultButton: 0,
                        onClose: () => resolve(APIActionStatus.passed)
                    }
                );
            } else {
                showFormClientSideMessage(
                    startRs.Session,
                    priority.netitems.shared.Message.Type.Warning,
                    {
                        confirmButtonText: htmlFormMessagesText.editLabel,
                        cancleButtonText: htmlFormMessagesText.readOnlyLabel,
                        title: htmlFormMessagesText.lockedByUser,
                        defaultButton: 1,
                        onConfirm: async () => {
                            const res = await unlockHtmlForm(startRs.Session);
                            if (res.Messages.length === 0) {
                                // remove lockBy property when unlock HTML form
                                startRs.LockBy = null;
                                resolve(APIActionStatus.passed);
                            }
                            resolve(APIActionStatus.blocked);
                        },
                        onClose: () => resolve(APIActionStatus.passed)
                    }
                );
            }
        }
    });
};

export const handleStartSubFormError = async (
    startRs: priority.netitems.form.IStartRs | null,
    parentForm: Form | null
) => {
    if (startRs?.Messages?.length > 0) {
        const isError =
            startRs.Messages?.[0]?.MType === priority.netitems.shared.Message.Type.Error;

        if (isError && parentForm) {
            const moveToParent = async () => {
                const activeScreen = formsStore.getScreenBySession(parentForm.session);

                if (parentForm.level !== activeScreen.activeLevel) {
                    await activeScreen.setActiveLevel(parentForm.level);
                }
            };

            return showError(startRs, false, {
                onErrorAccept: moveToParent,
                onClose: moveToParent
            });
        }
    }
    return APIActionStatus.passed;
};
